/**
 * CreditNoteDialog — raise a credit note against an issued invoice.
 *
 * Two paths share one dialog:
 *
 *   - **Full reversal** — the V1.0 default. Every invoice line is
 *     selected at full quantity; the CN reverses the entire invoice.
 *   - **Partial** — the operator unchecks lines or reduces quantities;
 *     the dialog sends a `lines` array and the server recomputes the
 *     CN's totals from those rows.
 *
 * Mode auto-detected from current selections — no separate toggle.
 * Default state is "everything selected, full qty" which matches the
 * old reason-only flow exactly. As soon as the operator unchecks a
 * row or reduces a qty, the dialog flips to partial in the wire
 * payload (`lines` becomes non-empty).
 */
import { useEffect, useMemo, useState } from "react";
import { FileMinus } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
  cn,
} from "@flint/ui";
import { paiseToInrShort } from "../../lib/format.ts";

export interface CreditNoteDialogLine {
  id: string;
  position: number;
  description: string;
  qtyMilli: number;
  unit: string;
  /** Stringified bigint of paise. */
  ratePaise: string;
  discountBps: number;
  taxRateBps: number;
  /** Stringified bigint of paise — the source line's already-computed line total. */
  lineTotalPaise: string;
}

export type CreditNoteDialogConfirm = {
  reason: string;
  /** Omitted when full reversal; populated for partial credits. */
  lines?: Array<{ sourceInvoiceLineId: string; qtyMilli: number }>;
};

export interface CreditNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display label of the parent invoice (`INV/2026-27/0001`). */
  invoiceDocNumber: string;
  /** Total amount the invoice was issued for (formatted ₹X,XXX). */
  invoiceTotalLabel: string;
  /** All issued lines on the parent invoice — drives the per-line picker. */
  lines: readonly CreditNoteDialogLine[];
  pending?: boolean;
  errorMessage?: string;
  onConfirm: (payload: CreditNoteDialogConfirm) => void;
}

const MIN_REASON_LEN = 3;

interface RowState {
  selected: boolean;
  /** Qty in milli units (qty × 1000). Defaults to the source line's qty. */
  qtyMilli: number;
}

export function CreditNoteDialog({
  open,
  onOpenChange,
  invoiceDocNumber,
  invoiceTotalLabel,
  lines,
  pending,
  errorMessage,
  onConfirm,
}: CreditNoteDialogProps) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [rows, setRows] = useState<Record<string, RowState>>({});

  // Reset whenever the dialog (re-)opens.
  useEffect(() => {
    if (!open) {
      setReason("");
      setTouched(false);
      setRows({});
      return;
    }
    const initial: Record<string, RowState> = {};
    for (const line of lines) {
      initial[line.id] = { selected: true, qtyMilli: line.qtyMilli };
    }
    setRows(initial);
  }, [open, lines]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < MIN_REASON_LEN;
  const showInline = touched && tooShort;

  // Live preview totals and "is this a partial?" flag.
  const { previewTotal, selectedCount, isPartial } = useMemo(() => {
    let total = 0n;
    let count = 0;
    let partial = false;
    for (const line of lines) {
      const state = rows[line.id];
      if (!state || !state.selected) {
        partial = true;
        continue;
      }
      const sourceQty = line.qtyMilli;
      if (state.qtyMilli !== sourceQty) partial = true;
      // Pro-rate the source line's total by qty ratio for the preview.
      // The server recomputes precisely via computeLineTax; the dialog's
      // job is only to give the operator a "this is roughly what you'll
      // credit" feel, so a ratio is good enough here.
      const lineTotal = BigInt(line.lineTotalPaise);
      const ratio = (BigInt(state.qtyMilli) * 10_000n) / BigInt(Math.max(sourceQty, 1));
      total += (lineTotal * ratio) / 10_000n;
      count += 1;
    }
    return { previewTotal: total, selectedCount: count, isPartial: partial };
  }, [lines, rows]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (tooShort) return;
    if (selectedCount === 0) return;
    const payload: CreditNoteDialogConfirm = { reason: trimmed };
    if (isPartial) {
      payload.lines = lines
        .filter((l) => rows[l.id]?.selected)
        .map((l) => ({
          sourceInvoiceLineId: l.id,
          qtyMilli: rows[l.id]?.qtyMilli ?? l.qtyMilli,
        }));
    }
    onConfirm(payload);
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-grid place-items-center h-9 w-9 rounded-md bg-amber-50 text-amber-700 shrink-0"
              >
                <FileMinus size={16} />
              </span>
              <div className="flex-1">
                <DialogTitle>Raise credit note?</DialogTitle>
                <DialogDescription>
                  Reverses lines on <span className="font-mono text-ink-2">{invoiceDocNumber}</span>{" "}
                  (total <span className="font-mono text-ink-2">{invoiceTotalLabel}</span>).
                  Append-only; cannot be edited later.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs text-muted">
              <span>
                {isPartial ? (
                  <>
                    <strong className="text-amber-700">Partial</strong> · {selectedCount} of{" "}
                    {lines.length} line{lines.length === 1 ? "" : "s"}
                  </>
                ) : (
                  <>
                    <strong className="text-ink">Full reversal</strong> · all {lines.length} line
                    {lines.length === 1 ? "" : "s"}
                  </>
                )}
              </span>
              <span>
                Credit total{" "}
                <span className="font-mono font-semibold text-ink">
                  {paiseToInrShort(previewTotal.toString())}
                </span>
              </span>
            </div>

            <div className="rounded-md border border-line overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-3 py-2 w-8" scope="col">
                      <span className="sr-only">Include</span>
                    </th>
                    <th className="px-3 py-2 text-left font-medium" scope="col">
                      Line
                    </th>
                    <th className="px-2 py-2 text-right font-medium w-[110px]" scope="col">
                      Return qty
                    </th>
                    <th className="px-3 py-2 text-right font-medium w-[110px]" scope="col">
                      Credit
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => {
                    const state = rows[line.id] ?? { selected: true, qtyMilli: line.qtyMilli };
                    return (
                      <tr
                        key={line.id}
                        className={cn(
                          "border-t border-line",
                          !state.selected && "bg-bg/50 text-muted",
                        )}
                      >
                        <td className="px-3 py-2 align-top">
                          <input
                            type="checkbox"
                            checked={state.selected}
                            onChange={(e) =>
                              setRows((prev) => ({
                                ...prev,
                                [line.id]: {
                                  selected: e.target.checked,
                                  qtyMilli: prev[line.id]?.qtyMilli ?? line.qtyMilli,
                                },
                              }))
                            }
                            aria-label={`Include line ${line.position}`}
                          />
                        </td>
                        <td className="px-3 py-2 align-top">
                          <div className="text-ink-2 leading-snug">{line.description}</div>
                          <div className="text-xs text-muted">
                            Original qty{" "}
                            <span className="font-mono">
                              {(line.qtyMilli / 1000)
                                .toFixed(line.qtyMilli % 1000 === 0 ? 0 : 3)
                                .replace(/\.?0+$/, "")}
                            </span>{" "}
                            {line.unit}
                          </div>
                        </td>
                        <td className="px-2 py-2 align-top">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={millisToText(state.qtyMilli)}
                            disabled={!state.selected}
                            onChange={(e) => {
                              const m = textToMillis(e.target.value);
                              if (m === null) return;
                              const clamped = Math.min(m, line.qtyMilli);
                              setRows((prev) => ({
                                ...prev,
                                [line.id]: {
                                  selected: prev[line.id]?.selected ?? true,
                                  qtyMilli: clamped,
                                },
                              }));
                            }}
                            className={cn(
                              "w-full text-right font-mono text-sm rounded-sm border border-line px-2 py-1",
                              "focus:outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100",
                              "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-bg",
                            )}
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-sm align-top">
                          {state.selected ? rowPreview(line, state.qtyMilli) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted leading-snug">
              Uncheck a line or reduce its qty for a partial credit. Server computes taxes from the
              source-line rates; this is a ratio preview.
            </p>
          </div>

          <div className="flex flex-col gap-1.5 mt-4">
            <Label htmlFor="cn-reason-input">Reason</Label>
            <Input
              id="cn-reason-input"
              autoFocus
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setTouched(true);
              }}
              maxLength={500}
              placeholder="Goods returned · Pricing error · Post-issue discount"
              aria-invalid={showInline}
              aria-describedby={showInline ? "cn-reason-error" : undefined}
            />
            {showInline ? (
              <p id="cn-reason-error" className="text-sm text-bad">
                Reason must be at least {MIN_REASON_LEN} characters.
              </p>
            ) : (
              <p className="text-sm text-muted">
                Printed on the customer-facing CN; lands in the audit log.
              </p>
            )}
            {errorMessage ? <p className="text-sm text-bad mt-1">{errorMessage}</p> : null}
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <Button
              type="button"
              intent="ghost"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              intent="primary"
              disabled={pending || tooShort || selectedCount === 0}
            >
              {pending ? "Raising…" : isPartial ? "Raise partial CN" : "Raise credit note"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}

function rowPreview(line: CreditNoteDialogLine, qtyMilli: number): string {
  if (qtyMilli === 0) return "₹0";
  const sourceQty = Math.max(line.qtyMilli, 1);
  const lineTotal = BigInt(line.lineTotalPaise);
  const ratio = (BigInt(qtyMilli) * 10_000n) / BigInt(sourceQty);
  const preview = (lineTotal * ratio) / 10_000n;
  return paiseToInrShort(preview.toString());
}

function millisToText(milli: number): string {
  if (milli === 0) return "0";
  const whole = Math.floor(milli / 1000);
  const frac = milli % 1000;
  if (frac === 0) return String(whole);
  return `${whole}.${String(frac).padStart(3, "0").replace(/0+$/, "")}`;
}

function textToMillis(text: string): number | null {
  const cleaned = text.replace(/[\s,]/g, "").trim();
  if (cleaned === "") return 0;
  if (!/^\d*(\.\d{0,3})?$/.test(cleaned)) return null;
  const [intPart, fracPart = ""] = cleaned.split(".");
  const padded = (fracPart + "000").slice(0, 3);
  return Number.parseInt(intPart || "0", 10) * 1000 + Number.parseInt(padded || "0", 10);
}
